import { Button, PageError, PageLoading, RecordDrawer, TextButton } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { Checkbox } from "@majitap/ui/arc/checkbox/checkbox";
import { ConfirmMorph } from "@majitap/ui/arc/confirm-morph/confirm-morph";
import { Input } from "@majitap/ui/arc/input/input";
import { SortableDataTable } from "@majitap/ui/arc/sortable-data-table/sortable-data-table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import { errorOf } from "../../api";
import { messageFor } from "../../error-copy";
import { useFormCheck } from "../../form-check";
import { type Permission, permissionGroups, permissionsQuery, type Role, rolesQuery, teamActions } from "../../team";
import { DrawerFooter } from "../shared/drawer-footer";
import styles from "./settings.module.css";

/**
 * Roles: the built-in ones (read-only, the same in every company) and the company's own. A
 * person's permissions are everything their roles grant together.
 */
export function RolesView(): ReactNode {
  const roles = useQuery(rolesQuery);
  const [editing, setEditing] = useState<Role | "new" | null>(null);
  if (roles.status === "pending") return <PageLoading label="Loading roles" />;
  if (roles.status === "error")
    return <PageError message={messageFor(errorOf(roles.error))} onRetry={() => roles.refetch()} />;
  return (
    <div className={styles.panel}>
      <div className={styles.actions}>
        <Button variant="secondary" size="sm" onClick={() => setEditing("new")}>
          New role
        </Button>
      </div>
      <SortableDataTable
        caption="Roles"
        rowKey="id"
        rows={roles.data}
        itemName={{ one: "role", other: "roles" }}
        columns={[
          {
            key: "name",
            label: "Role",
            sortable: false,
            render: (_v, r) => (
              <span className={styles.stack}>
                <span>
                  {r.name} {r.is_system ? <Badge tone="info">Built in</Badge> : null}
                </span>
                {r.description ? <span className={styles.muted}>{r.description}</span> : null}
              </span>
            ),
          },
          {
            key: "permissions",
            label: "Permissions",
            sortable: false,
            render: (_v, r) => <span className={styles.muted}>{r.permissions.join(", ") || "None"}</span>,
          },
          {
            key: "actions",
            label: "",
            sortable: false,
            render: (_v, r) => <TextButton onClick={() => setEditing(r)}>{r.is_system ? "View" : "Edit"}</TextButton>,
          },
        ]}
      />
      {editing ? <RoleDialog role={editing === "new" ? undefined : editing} onClose={() => setEditing(null)} /> : null}
    </div>
  );
}

/** A role's permissions as a checklist grouped by area, each with what it lets someone do. */
export function PermissionChecklist({
  permissions,
  value,
  onChange,
  readOnly = false,
}: {
  permissions: Permission[];
  value: string[];
  onChange: (ids: string[]) => void;
  readOnly?: boolean;
}) {
  return (
    <div className={styles.groups}>
      {permissionGroups(permissions).map((group) => (
        <fieldset key={group.area} className={styles.fieldset}>
          <legend className={styles.legend}>{group.area}</legend>
          {group.permissions.map((p) => (
            <Checkbox
              key={p.id}
              label={p.description ?? p.name}
              description={p.id}
              checked={value.includes(p.id)}
              disabled={readOnly}
              onCheckedChange={(on) => onChange(on ? [...value, p.id] : value.filter((id) => id !== p.id))}
            />
          ))}
        </fieldset>
      ))}
    </div>
  );
}

function RoleDialog({ role, onClose }: { role?: Role; onClose: () => void }) {
  const queryClient = useQueryClient();
  const catalogue = useQuery(permissionsQuery);
  const [name, setName] = useState(role?.name ?? "");
  const [description, setDescription] = useState(role?.description ?? "");
  const [chosen, setChosen] = useState<string[]>(role?.permissions ?? []);
  const readOnly = role?.is_system ?? false;
  const check = useFormCheck();
  const errors = { name: name.trim() ? undefined : "Name the role." };
  const remove = useMutation({
    mutationFn: () => teamActions.deleteRole(role?.id ?? ""),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["staff"] });
      onClose();
    },
  });
  const save = useMutation({
    mutationFn: () =>
      role
        ? teamActions.updateRole(role.id, {
            name: name.trim(),
            description: description.trim() || null,
            permissions: chosen,
          })
        : teamActions.createRole({
            name: name.trim(),
            description: description.trim() || undefined,
            permissions: chosen,
          }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["staff"] });
      onClose();
    },
  });
  return (
    <RecordDrawer
      open
      onOpenChange={(o) => !o && onClose()}
      title={role ? (readOnly ? role.name : `Edit ${role.name}`) : "New role"}
      description={
        readOnly
          ? "A built-in role: the same in every company, so it can't change. Make your own role for something else."
          : "People with this role get these permissions, on top of their other roles. They take effect when they next load a page."
      }
    >
      <div className={styles.form}>
        {readOnly ? null : (
          <div className={styles.pair}>
            <Input
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Night dispatcher"
              error={check.show(errors.name)}
            />
            <Input
              label="Description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        )}
        {catalogue.data ? (
          <PermissionChecklist permissions={catalogue.data} value={chosen} onChange={setChosen} readOnly={readOnly} />
        ) : catalogue.isError ? (
          <Alert tone="danger" title="The permissions didn't load">
            {messageFor(errorOf(catalogue.error))}{" "}
            <TextButton onClick={() => void catalogue.refetch()}>Try again</TextButton>
          </Alert>
        ) : (
          <p className={styles.muted}>Loading permissions…</p>
        )}
        {save.error ? (
          <Alert tone="danger" title="Not saved">
            {messageFor(errorOf(save.error))}
          </Alert>
        ) : null}
        {remove.error ? (
          <Alert tone="danger" title="Not removed">
            {messageFor(errorOf(remove.error))}
          </Alert>
        ) : null}
        <DrawerFooter
          start={
            role && !readOnly ? (
              <ConfirmMorph
                label="Remove the role"
                prompt="Remove it?"
                confirmLabel="Remove role"
                cancelLabel="Keep"
                pendingLabel="Removing"
                doneLabel="Removed"
                errorLabel="Not removed"
                tone="danger"
                onConfirm={() => remove.mutateAsync()}
              />
            ) : undefined
          }
        >
          <Button variant="secondary" onClick={onClose}>
            {readOnly ? "Close" : "Cancel"}
          </Button>
          {readOnly ? null : (
            <Button loading={save.isPending} onClick={() => check.submit(errors, () => save.mutate())}>
              {role ? "Save role" : "Make the role"}
            </Button>
          )}
        </DrawerFooter>
      </div>
    </RecordDrawer>
  );
}
